import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoadingService } from './loading.service';
import { ToastService } from './toast.service';
import { environment as env } from '../../environments/environment';

/**
 * Customer directory API client. CRUD against
 * f2-admin-service2's /rest/admin/customer-directory endpoints
 * (F2-ADMIN.Customers collection). Super-admin only on the backend —
 * the frontend route guard mirrors that.
 */
@Injectable({ providedIn: 'root' })
export class CustomerDirectoryService {
  constructor(
    private http: HttpClient,
    private loadingService: LoadingService,
    private toast: ToastService
  ) {}

  private _opts() {
    return { withCredentials: true };
  }

  async list(): Promise<any[]> {
    const id = this.loadingService.add('Loading customer directory');
    try {
      const res: any = await this.http
        .get(`${env.apiUrl}/rest/admin/customer-directory`, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Customer directory', res.err);
        return [];
      }
      return Array.isArray(res) ? res : [];
    } catch (e: any) {
      this.loadingService.remove(id);
      this.toast.error('Customer directory', e.message);
      return [];
    }
  }

  // Recompute per-customer user counts on the backend. Heavy on Cognito
  // user-cache size; intentionally admin-triggered, not auto.
  async refreshClientCounts(): Promise<any> {
    const id = this.loadingService.add('Refreshing client counts');
    try {
      const res: any = await this.http
        .post(`${env.apiUrl}/rest/admin/customer-directory/refresh-client-counts`, {}, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Refresh failed', res.err);
        return res;
      }
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      this.toast.error('Refresh failed', e.message);
      return { err: e.message };
    }
  }

  async create(record: any): Promise<any> {
    const id = this.loadingService.add('Creating customer');
    try {
      const res: any = await this.http
        .post(`${env.apiUrl}/rest/admin/customer-directory`, record, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Create failed', res.err);
        return res;
      }
      this.toast.success('Customer created', record._id);
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      this.toast.error('Create failed', e.message);
      return { err: e.message };
    }
  }

  async update(slug: string, record: any): Promise<any> {
    const id = this.loadingService.add('Updating customer');
    try {
      const res: any = await this.http
        .put(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}`, record, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Update failed', res.err);
        return res;
      }
      this.toast.success('Customer updated', slug);
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      this.toast.error('Update failed', e.message);
      return { err: e.message };
    }
  }

  async remove(slug: string): Promise<any> {
    const id = this.loadingService.add('Deleting customer');
    try {
      const res: any = await this.http
        .delete(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}`, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Delete failed', res.err);
        return res;
      }
      this.toast.success('Customer deleted', slug);
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      this.toast.error('Delete failed', e.message);
      return { err: e.message };
    }
  }

  // ----- SSO portal shared-secret management (super-admin) -----
  // Mirrors the f2-members IntegrationSettings page; F2 super-admin scope.
  // Plaintext arrives only in the response to generateSecret(); list reads
  // never reveal it.

  async listSsoSecrets(slug: string): Promise<Array<{ id: string; fingerprint: string; length: number; label?: string; created_at: string; created_by_email?: string }>> {
    try {
      const res: any = await this.http
        .get(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/sso-portal/secrets`, this._opts())
        .toPromise();
      if (res?.err) {
        this.toast.error('Load secrets failed', res.err);
        return [];
      }
      return Array.isArray(res) ? res : [];
    } catch (e: any) {
      this.toast.error('Load secrets failed', e.message);
      return [];
    }
  }

  async generateSsoSecret(slug: string, label?: string): Promise<{ id: string; secret: string; fingerprint: string; length: number; created_at: string } | { err: string }> {
    const id = this.loadingService.add('Generating secret');
    try {
      const res: any = await this.http
        .post(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/sso-portal/secrets`, { label }, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Generate failed', res.err);
        return res;
      }
      this.toast.success('Secret generated', `fp: ${res.fingerprint}`);
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      this.toast.error('Generate failed', e.message);
      return { err: e.message };
    }
  }

  async importSsoSecret(slug: string, plaintext: string, label?: string): Promise<{ id: string; fingerprint: string; length: number; created_at: string } | { err: string }> {
    const id = this.loadingService.add('Importing secret');
    try {
      const res: any = await this.http
        .post(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/sso-portal/secrets/import`, { plaintext, label }, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Import failed', res.err);
        return res;
      }
      this.toast.success('Secret imported', `fp: ${res.fingerprint}`);
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      this.toast.error('Import failed', e.message);
      return { err: e.message };
    }
  }

  /** Read the per-customer stored override password (decrypted plaintext).
   *  Returns null when no value is stored yet for this customer. */
  async getStoredOverridePassword(slug: string): Promise<{ password: string; set_at: string | null; set_by_email: string | null } | null> {
    try {
      const res: any = await this.http
        .get(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/stored-override-password`, this._opts())
        .toPromise();
      if (res?.err) return null;
      if (res?.password === undefined) return null;
      return res;
    } catch {
      return null;
    }
  }

  /** Explicitly rotate the per-customer stored override password without
   *  applying it to any user. Mirrors the side-effect that
   *  /override_password fires on successful AdminSetUserPassword. */
  async setStoredOverridePassword(slug: string, password: string): Promise<{ ok: boolean; err?: string }> {
    try {
      const res: any = await this.http
        .post(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/stored-override-password`, { password }, this._opts())
        .toPromise();
      if (res?.err) {
        this.toast.error('Rotate failed', res.err);
        return { ok: false, err: res.err };
      }
      return { ok: !!res?.ok };
    } catch (e: any) {
      this.toast.error('Rotate failed', e.message);
      return { ok: false, err: e.message };
    }
  }

  async deleteSsoSecret(slug: string, secretId: string): Promise<any> {
    const id = this.loadingService.add('Deleting secret');
    try {
      const res: any = await this.http
        .delete(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/sso-portal/secrets/${encodeURIComponent(secretId)}`, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Delete failed', res.err);
        return res;
      }
      this.toast.success('Secret deleted', secretId.slice(0, 8) + '…');
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      this.toast.error('Delete failed', e.message);
      return { err: e.message };
    }
  }

  // ----- Commerce integrations (WooCommerce, Stripe, future) -----
  // Each integration owns: enabled toggle, webhook_secret (write-only on
  // non-secret PUT to avoid wipe; rotate via dedicated secret endpoints),
  // default_scanner, product_map.

  async getCommerce(slug: string): Promise<any> {
    try {
      const res: any = await this.http
        .get(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce`, this._opts())
        .toPromise();
      if (res?.err) {
        this.toast.error('Load commerce config failed', res.err);
        return null;
      }
      return res;
    } catch (e: any) {
      this.toast.error('Load commerce config failed', e.message);
      return null;
    }
  }

  async revealCommerceSecret(slug: string, integration: string): Promise<{ secret: string | null } | { err: string }> {
    try {
      const res: any = await this.http
        .get(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/${encodeURIComponent(integration)}/secret`, this._opts())
        .toPromise();
      if (res?.err) {
        this.toast.error('Reveal secret failed', res.err);
        return res;
      }
      return res;
    } catch (e: any) {
      this.toast.error('Reveal secret failed', e.message);
      return { err: e.message };
    }
  }

  async getCommerceSettings(slug: string): Promise<any> {
    try {
      const res: any = await this.http
        .get(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/shared`, this._opts())
        .toPromise();
      return res;
    } catch (e: any) {
      return { err: e.message };
    }
  }

  async updateCommerceSettings(slug: string, body: any): Promise<any> {
    const id = this.loadingService.add('Saving shared commerce settings');
    try {
      const res: any = await this.http
        .put(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/shared`, body, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Save failed', res.err);
        return res;
      }
      this.toast.success('Shared commerce settings saved', slug);
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      this.toast.error('Save failed', e.message);
      return { err: e.message };
    }
  }

  // Mike ask 2026-08-05: dynamic per-product past-buyer counts for the
  // Copy-to-Excel button on the SamCart Product Map editor. Called on
  // click (not on popup load) so counts reflect fresh CommerceEvents
  // state. Backend does the mongo aggregation.
  async getPastBuyerCounts(slug: string, integration: string): Promise<Record<string, number>> {
    try {
      const res: any = await this.http
        .get(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/${encodeURIComponent(integration)}/past-buyer-counts`, this._opts())
        .toPromise();
      if (res?.err) return {};
      const counts = res?.counts;
      return counts && typeof counts === 'object' ? counts : {};
    } catch {
      return {};
    }
  }

  async updateCommerce(slug: string, integration: string, body: any): Promise<any> {
    const id = this.loadingService.add('Saving commerce config');
    try {
      const res: any = await this.http
        .put(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/${encodeURIComponent(integration)}`, body, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Save failed', res.err);
        return res;
      }
      this.toast.success('Commerce config saved', integration);
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      this.toast.error('Save failed', e.message);
      return { err: e.message };
    }
  }

  // IT-F2-432 c/5e429a80 — "Do we have a test button for it in the api
  // form?". Fires a lightweight SamCart /v1/me probe with an optional
  // in-flight token so operators can verify a freshly-pasted key before
  // committing to a Save that runs product_map remap sweeps. Backend
  // never writes anything on this call.
  async testSamcartToken(slug: string, body: { token?: string }): Promise<any> {
    const id = this.loadingService.add('Testing SamCart token');
    try {
      const res: any = await this.http
        .post(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/samcart/test`, body, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      // Pass the raw error through — the component surfaces it in the
      // red-X probe-result pill instead of a toast so operators can see
      // the HTTP status + body without context-switching.
      return { ok: false, message: e?.message || 'Request threw', status: e?.status };
    }
  }

  async setCommerceSecret(slug: string, integration: string, body: { plaintext?: string; generate?: boolean }): Promise<{ ok: true; secret: string } | { err: string }> {
    const id = this.loadingService.add(body.generate ? 'Generating secret' : 'Setting secret');
    try {
      const res: any = await this.http
        .post(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/${encodeURIComponent(integration)}/secret`, body, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Set secret failed', res.err);
        return res;
      }
      this.toast.success('Secret saved', integration);
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      this.toast.error('Set secret failed', e.message);
      return { err: e.message };
    }
  }

  async clearCommerceSecret(slug: string, integration: string): Promise<any> {
    const id = this.loadingService.add('Clearing secret');
    try {
      const res: any = await this.http
        .delete(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/${encodeURIComponent(integration)}/secret`, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Clear failed', res.err);
        return res;
      }
      this.toast.success('Secret cleared', integration);
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      this.toast.error('Clear failed', e.message);
      return { err: e.message };
    }
  }

  async clearCommercePingStats(slug: string, integration: string): Promise<any> {
    const id = this.loadingService.add('Clearing ping stats');
    try {
      const res: any = await this.http
        .delete(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/${encodeURIComponent(integration)}/ping-stats`, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) {
        this.toast.error('Reset failed', res.err);
        return res;
      }
      this.toast.success('Ping stats reset', integration);
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      this.toast.error('Reset failed', e.message);
      return { err: e.message };
    }
  }

  async revealCommerceConsumerSecret(slug: string, integration: string): Promise<{ secret: string | null } | { err: string }> {
    try {
      const res: any = await this.http
        .get(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/${encodeURIComponent(integration)}/consumer-secret`, this._opts())
        .toPromise();
      if (res?.err) {
        this.toast.error('Reveal failed', res.err);
        return res;
      }
      return res;
    } catch (e: any) {
      this.toast.error('Reveal failed', e.message);
      return { err: e.message };
    }
  }

  // Download every subscription from the merchant's WC REST API.
  //
  // Uses the NDJSON streaming endpoint so the loading overlay can show
  // real page-by-page progress. Backend emits one `{kind:'page', page,
  // fetched, more}` line per completed WC API page, then a final
  // `{kind:'result', ok, subscriptions, total, pages}` line. Reader
  // buffers by newline; on each page event we call loadingService.
  // setProgress with the running count. Falls back to HttpClient on
  // any streaming-side failure so the operation still completes even
  // if the browser lacks ReadableStream support (very unlikely on
  // supported browsers, but a safe belt).
  async fetchWcSubscriptions(slug: string): Promise<{ ok: boolean; subscriptions?: any[]; err?: string; pages?: number; total?: number }> {
    const id = this.loadingService.add('Downloading WC subscriptions');
    try {
      const url = `${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/woocommerce/wc-subscriptions/stream`;
      const resp = await fetch(url, { method: 'GET', credentials: 'include' });
      if (!resp.ok || !resp.body) throw new Error(`stream ${resp.status} ${resp.statusText}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let last: any = null;
      let pageCount = 0;
      // Best-guess total pages (unknown until the last page). Pin
      // progress fraction using a heuristic: assume ~10 pages on the
      // low end so the bar fills visibly early, then stretches as more
      // pages arrive. Actual bar tops out at ~95% until the result
      // line lands (which flips to 100%).
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let nl = buffer.indexOf('\n');
        while (nl >= 0) {
          const line = buffer.slice(0, nl).trim();
          buffer = buffer.slice(nl + 1);
          nl = buffer.indexOf('\n');
          if (!line) continue;
          try {
            const evt = JSON.parse(line);
            if (evt?.kind === 'page') {
              pageCount++;
              const estTotal = Math.max(pageCount + 1, 10);
              const frac = Math.min(0.95, pageCount / estTotal);
              this.loadingService.setProgress(id, frac, `${evt.fetched} subscription(s) across ${pageCount} page(s)`);
            } else if (evt?.kind === 'result') {
              this.loadingService.setProgress(id, 1, `${evt.total || 0} subscription(s) across ${evt.pages || pageCount} page(s)`);
              last = evt;
            } else if (evt?.kind === 'error') {
              throw new Error(evt.err || 'stream error');
            }
          } catch (e) {
            // Malformed line — log and continue; the final result line
            // is what matters.
            console.warn('WC stream: malformed line', e);
          }
        }
      }
      this.loadingService.remove(id);
      if (!last || last.ok === false || last.err) {
        const err = last?.err || 'stream did not return a result line';
        this.toast.error('Download failed', err);
        return { ok: false, err };
      }
      // Drop the streaming wrapper's `kind` field so the caller sees
      // the same shape the non-streaming path returned.
      const { kind, ...rest } = last;
      return rest;
    } catch (e: any) {
      this.loadingService.remove(id);
      // Fallback to the non-streaming path so a one-off stream failure
      // (proxy strips the connection, browser blocks the body reader
      // on a niche mobile, etc.) still lets the download succeed.
      try {
        const id2 = this.loadingService.add('Downloading WC subscriptions (fallback)');
        const res: any = await this.http
          .get(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/woocommerce/wc-subscriptions`, this._opts())
          .toPromise();
        this.loadingService.remove(id2);
        if (res?.err || res?.ok === false) {
          this.toast.error('Download failed', res?.err || 'unknown error');
          return { ok: false, err: res?.err };
        }
        return res;
      } catch (e2: any) {
        this.toast.error('Download failed', e2?.message || e?.message || 'unknown');
        return { ok: false, err: e2?.message || e?.message || 'unknown' };
      }
    }
  }

  // Sync WC subscriptions to F2 entitlement state. Each action is
  // logged as a CommerceEvents row server-side.
  async reconcileWcSubscriptions(slug: string): Promise<any> {
    const id = this.loadingService.add('Reconciling WC subscriptions');
    try {
      const res: any = await this.http
        .post(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/woocommerce/reconcile`, {}, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err || res?.ok === false) {
        this.toast.error('Reconcile failed', res?.err || 'unknown error');
        return { ok: false, err: res?.err };
      }
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      this.toast.error('Reconcile failed', e.message);
      return { ok: false, err: e.message };
    }
  }

  // { [wc_product_id]: product_name } map derived from the most recent
  // wc-subscriptions-<slug>-<YYYY-MM-DD> cache. Nice-to-have context for
  // the product-map editor's "Product name" column so ops speaks the
  // customer's language instead of hand-cross-referencing numeric ids.
  // Silent on failure (returns empty map) -- the mapping still renders
  // with just the id column.
  async getWcProductNames(slug: string): Promise<Record<string, string>> {
    try {
      const res: any = await this.http
        .get(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/woocommerce/wc-product-names`, this._opts())
        .toPromise();
      if (res && res.ok && res.names && typeof res.names === 'object') {
        return res.names as Record<string, string>;
      }
      return {};
    } catch {
      return {};
    }
  }

  // SamCart sibling — uses the merchant's SamCart REST API token stored
  // at F2-ADMIN.Customers.<slug>.samcart.api_token to pull every
  // subscription, identical contract to the WC fetch.
  async fetchSamcartSubscriptions(slug: string): Promise<{ ok: boolean; subscriptions?: any[]; err?: string; pages?: number; total?: number }> {
    const id = this.loadingService.add('Downloading SamCart subscriptions');
    try {
      const res: any = await this.http
        .get(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/samcart/samcart-subscriptions`, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err || res?.ok === false) {
        this.toast.error('Download failed', res?.err || 'unknown error');
        return { ok: false, err: res?.err };
      }
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      this.toast.error('Download failed', e.message);
      return { ok: false, err: e.message };
    }
  }

  // SamCart historical snapshots (IT-F2-198 cid a73ce3bf). Snapshots are
  // the single source of truth for downloads + reconciliation; the old
  // ?page=N chunked path was removed in Phase 1 after f2-session's cursor
  // pagination fix made it a 100-sub trap.
  async listSamcartSnapshots(
    slug: string,
  ): Promise<{ ok: boolean; snapshots?: any[]; err?: string }> {
    try {
      const res: any = await this.http
        .get(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/samcart/snapshots`, this._opts())
        .toPromise();
      if (res?.err || res?.ok === false) {
        return { ok: false, err: res?.err || 'unknown error' };
      }
      return res;
    } catch (e: any) {
      return { ok: false, err: e?.message || 'network error' };
    }
  }

  async getSamcartSnapshot(
    slug: string,
    runId: string,
    includeRaw?: boolean,
  ): Promise<{ ok: boolean; snapshot?: any; err?: string }> {
    try {
      const qs = includeRaw ? '?include=raw' : '';
      const res: any = await this.http
        .get(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/samcart/snapshots/${encodeURIComponent(runId)}${qs}`, this._opts())
        .toPromise();
      if (res?.err || res?.ok === false) {
        return { ok: false, err: res?.err || 'unknown error' };
      }
      return res;
    } catch (e: any) {
      return { ok: false, err: e?.message || 'network error' };
    }
  }

  async triggerSamcartSyncHistorical(
    slug: string,
  ): Promise<{ ok: boolean; run_id?: string; status?: string; err?: string } & Record<string, any>> {
    const id = this.loadingService.add('Running SamCart historical sync (full sweep)');
    try {
      const res: any = await this.http
        .post(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/samcart/sync-historical`, {}, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err || res?.ok === false) {
        return { ok: false, err: res?.err || 'unknown error' };
      }
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      return { ok: false, err: e?.message || 'network error' };
    }
  }

  async getSamcartSnapshotStatus(
    slug: string,
    runId: string,
  ): Promise<{ ok: boolean; status?: string; subs_total?: number; orders_total?: number; subs_pages?: number; orders_pages?: number; purchases_total?: number; started_at?: string; updated_at?: string; errors?: string[]; err?: string }> {
    try {
      const res: any = await this.http
        .get(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/samcart/snapshots/${encodeURIComponent(runId)}/status`, this._opts())
        .toPromise();
      if (res?.err || res?.ok === false) {
        return { ok: false, err: res?.err || 'unknown error' };
      }
      return res;
    } catch (e: any) {
      return { ok: false, err: e?.message || 'network error' };
    }
  }

  async reconcileSamcartSubscriptions(slug: string): Promise<any> {
    const id = this.loadingService.add('Reconciling SamCart subscriptions');
    try {
      const res: any = await this.http
        .post(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/commerce/samcart/reconcile`, {}, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err || res?.ok === false) {
        this.toast.error('Reconcile failed', res?.err || 'unknown error');
        return { ok: false, err: res?.err };
      }
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      this.toast.error('Reconcile failed', e.message);
      return { ok: false, err: e.message };
    }
  }

  // ----- Email provider (Customer.io etc.) -----
  async getEmailProvider(slug: string): Promise<any> {
    try {
      const res: any = await this.http
        .get(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/email-provider`, this._opts())
        .toPromise();
      if (res?.err) { this.toast.error('Load email provider failed', res.err); return null; }
      return res;
    } catch (e: any) { this.toast.error('Load email provider failed', e.message); return null; }
  }

  async updateEmailProvider(slug: string, body: any): Promise<any> {
    const id = this.loadingService.add('Saving email provider');
    try {
      const res: any = await this.http
        .put(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/email-provider`, body, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) { this.toast.error('Save failed', res.err); return res; }
      this.toast.success('Email provider saved', slug);
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      this.toast.error('Save failed', e.message);
      return { err: e.message };
    }
  }

  async revealEmailProviderApiKey(slug: string): Promise<{ api_key: string | null } | { err: string }> {
    try {
      const res: any = await this.http
        .get(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/email-provider/api-key`, this._opts())
        .toPromise();
      if (res?.err) { this.toast.error('Reveal failed', res.err); return res; }
      return res;
    } catch (e: any) { this.toast.error('Reveal failed', e.message); return { err: e.message }; }
  }

  async clearEmailProviderApiKey(slug: string): Promise<any> {
    const id = this.loadingService.add('Clearing email API key');
    try {
      const res: any = await this.http
        .delete(`${env.apiUrl}/rest/admin/customer-directory/${encodeURIComponent(slug)}/email-provider/api-key`, this._opts())
        .toPromise();
      this.loadingService.remove(id);
      if (res?.err) { this.toast.error('Clear failed', res.err); return res; }
      this.toast.success('Email API key cleared', slug);
      return res;
    } catch (e: any) {
      this.loadingService.remove(id);
      this.toast.error('Clear failed', e.message);
      return { err: e.message };
    }
  }
}
